/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import {ColDef, ColumnState} from 'ag-grid-community';
import {AgGridReact} from 'ag-grid-react';
import {globalGroupIdSelector} from '@ion/applications/src/store/selectors/global-group';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  getMigrationResultsById,
  getMigrationStatus,
  StartScheduleMigration
} from '@ion/core/src/services/Reports';
import {
  MigrationResult,
  MigrationResults
} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import CTALink from '@ion/dls/src/components/buttons/CTALink';
import {IconButtonLabelPlacement} from '@ion/dls/src/components/buttons/IconButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import SearchField from '@ion/dls/src/components/search/SearchField';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {
  defaultDateFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {
  definitionsToState,
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import IonGrid, {AgGridType, IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import useColumnChooser from '@ion/dls/src/grid/useColumnChooser';
import useColumnState from '@ion/dls/src/grid/useColumnState';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import withSignals from '../../components/hoc/withSignals';
import {dispatchGridColumnsChange} from '../../store/helpers';
import {gridDefaultPageSize} from '../../utils/helpers';
import MigrationDetailsGrid from './MigrationDetailsGrid';
import {
  migrationGridColumnState,
  setMigrationGridColumnState
} from './store';
import useLoadMigrationResults from './useLoadMigrationResults';

/**
 * Props for the MigrationResultsGrid component.
 */
interface MigrationResultsGridProps {
  viewSubGroups?: boolean;
  rowMenusEnabled?: boolean;
  omitHeader?: boolean;
  omitHeaderCTAs?: boolean;
  includeGlobalSearch?: boolean;
  toggleViewSubGroups?: Function;
  gridPageSize: number;
  user?: any;
}

interface SnackbarState {
  show: boolean;
  message: string;
  type?: string;
  isIcon?: boolean;
  autoHideDuration?: number;
}

const spaceRegex = /,|\s|\s,|,\s/g;

const useStyles = makeStyles((theme) => ({
  endAdornment : {
    display              : 'flex',
    padding              : '0 10px',
    cursor               : 'pointer',
    '& .MuiSvgIcon-root' : {
      fontSize : 16
    }
  },
  agDetailsRow : {
    display : 'block !important'
  },
  migrateControl : {
    marginBottom : '10px'
  },
  search : {
    width                          : '100%',
    alignItems                     : 'center',
    justifyContent                 : 'space-between',
    gap                            : '4px',
    display                        : 'flex',
    marginBottom                   : '20px',
    flexDirection                  : 'row',
    [theme.breakpoints.down('sm')] : {
      width         : '100%',
      flexDirection : 'column',
      alignItems    : 'start'
    }
  },
  searchControls : {
    width                          : '536px',
    alignItems                     : 'center',
    display                        : 'flex',
    gap                            : '4px',
    flexDirection                  : 'row',
    [theme.breakpoints.down('sm')] : {
      width         : '100%',
      flexDirection : 'column',
      alignItems    : 'start'
    }
  },
  searchType : {
    width        : '264px',
    paddingRight : '20px !important'
  },
  searchTypeInput : {
    padding  : '5px 2em !important',
    fontSize : '14px'
  },
  searchTypeOption : {
    fontSize : '14px !important'
  },
  searchInput : {
    fontSize : '14px !important'
  },
  clearFilters : {
    ...dlsTypography('cta'),
    color         : dlsColor('caBlue'),
    cursor        : 'pointer',
    letterSpacing : '1px',
    fontSize      : '13px',
    padding       : '12px',
    display       : 'block',
    whiteSpace    : 'nowrap',
    '&&:hover'    : {
      textDecoration : 'underline'
    },
    [theme.breakpoints.down('sm')] : {
      paddingLeft : 0
    }
  }
}));

// name of the reducer
export const NAME = 'migrationResults';

// grid column state
export const SET_GRID_COLUMNS = 'SET_MIGRATION_RESULTS_GRID_COLUMNS';

/**
 * MigrationResultsGrid that implements the AgGridRect to display the
 * migration status and results.
 */
const MigrationResultsGrid = forwardRef<IonGridAPI, MigrationResultsGridProps>(({
  viewSubGroups,
  toggleViewSubGroups,
  user,
  rowMenusEnabled = true,
  omitHeader = false,
  omitHeaderCTAs = false,
  includeGlobalSearch = false,
  gridPageSize = gridDefaultPageSize
}: MigrationResultsGridProps, ref) => {
  const classes               = useStyles();
  const [gridApi, setGridApi] = useState<AgGridType>(null);
  const [rowData, setRowData] = useState<Array>([]);
  const [loading, setLoading] = useState(false);
  const columnState           = migrationGridColumnState.value;
  const ionGridRef            = useRef<IonGridAPI>();
  const dispatch              = useDispatch();
  const globalAccountId       = useSelector(globalGroupIdSelector);
  const isPhone               = useIsPhone();
  const gridRef               = useRef(null);

  const [refreshRequired, setRefreshRequired] = useState(false);
  const [snackbarState, setSnackbarState]     = useState<SnackbarState>({
    show    : false,
    message : ''
  });

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  const {getAllMigrationResults, refresh, onRefresh, filterModel} =
    useLoadMigrationResults({
      gridApi,
      globalAccountId,
      viewSubGroups,
      searchValue : ''
    });

  const refreshMigrationResults = useCallback(() => {
    onRefresh?.();
    ionGridRef.current?.refreshCurrentBlock?.();
  }, [onRefresh]);

  useImperativeHandle(ref, () => ({
    ...ionGridRef.current,
    onRefresh           : refreshMigrationResults,
    refreshCurrentBlock : refreshMigrationResults
  }), [refreshMigrationResults]);

  const fetchData = async() => {
    try {
      setLoading(true);
      gridRef.current?.api?.showLoadingOverlay();
      const result = await getMigrationStatus({});
      const data = result?.migrationResults || [];
      setRowData(data);
      if (!data.length) {
        gridRef.current?.api?.showNoRowsOverlay();
      } else {
        gridRef.current?.api?.hideOverlay();
      }
    } catch (err) {
      error(`Error occured while getMigrationStatus call, Error, ${err.toString()}`);
      gridRef.current?.api?.showNoRowsOverlay();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshRequired]);

  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const clearSnackbar = useCallback(() => {
    setSnackbarState((prevState) => ({
      ...prevState,
      show : false
    }));
  }, []);

  const fetchCallRecords = async(id) => {
    try {
      const response = await getMigrationResultsById({migrationId: id});
      return response?.migrationDetails;
    } catch (err) {
      error(`Error occured while getMigrationResultsById call, Error, ${err.toString()}`);
      return [];
    }
  };

  // Configuration for the Detail Grid
  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions : {
        columnDefs    : getColumnDefinitionsMigrationResultsDesktop(),
        defaultColDef : {flex: 1}
      },
      // Maps master data to detail grid rows
      getDetailRowData : async(params) => {
        const id = params.data.migrationId;

        try {
          const result = await fetchCallRecords(id);

          // optional: cache in rowData
          params.data.callRecords = result;

          params.successCallback(result);
        } catch (err) {
          params.successCallback([]);
        }
      }
    };
  }, []);

  async function onAdd() {
    const migrateInfo = {
      migrationType : 'byAccount',
      accountIds    : [
        entityId(globalAccountId)
      ],
      includeSubaccounts : true,
      notificationEmails : [
        user.email
      ]
    };
    try {
      await StartScheduleMigration(migrateInfo);
      setSnackbarState({
        show             : true,
        message          : translate('sisense:analytics.migrationScheduleSuccess'),
        autoHideDuration : 3000
      });
    } catch (err) {
      error(`Error occured while StartScheduleMigration call, Error, ${err.toString()}`);
      setSnackbarState({
        show             : true,
        message          : translate('sisense:analytics.migrationScheduleError'),
        type             : 'error',
        isIcon           : true,
        autoHideDuration : 5000
      });
    }
  }

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
        <div className={isCurrentTheme ? classes.migrateControl : ''}>
          <PrimaryButton onClick={onAdd}>
            <span>{translate('sisense:analytics.migrateSchedule')}</span>
          </PrimaryButton>
        </div>
        <div>
          <RefreshGridButton onRefresh={handleRefresh}
            currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
        </div>
      </div>
      <div className="ag-theme-alpine" style={{height: 400, width: 'auto'}}>
        <AgGridReact rowData={rowData}
          ref={gridRef}
          suppressContextMenu={true}
          columnDefs={getColumnDefinitionsDesktop()}
          masterDetail
          overlayLoadingTemplate={
            '<span class="ag-overlay-loading-center">Refreshing data...</span>'
          }
          overlayNoRowsTemplate={
            '<span>No migration results found</span>'
          }
          detailCellRendererParams={detailCellRendererParams} />
      </div>
      <IonSnackbar show={snackbarState.show}
        type={snackbarState.type}
        isIcon={snackbarState.isIcon}
        message={snackbarState.message}
        actionLabel={translate('common:clear')}
        onAction={clearSnackbar}
        onClose={clearSnackbar}
        autoHideDuration={snackbarState.autoHideDuration} />
    </>
  );
});

/**
 * @getColumnDefinitionsDesktop
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [
    {
      headerName               : translate('sisense:analytics.migrationId'),
      field                    : 'migrationId',
      cellRenderer             : 'agGroupCellRenderer',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.migrationType'),
      field                    : 'migrationType',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.accountIds'),
      field                    : 'accountIds',
      valueFormatter           : (params) => params.value.join(', '),
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.includeSubaccount'),
      field                    : 'includeSubaccount',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.total'),
      field                    : 'total',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.success'),
      field                    : 'success',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.failed'),
      field                    : 'failed',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.skipped'),
      field                    : 'skipped',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.status'),
      field                    : 'status',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.created'),
      field                    : 'created',
      valueFormatter           : (params) => formatDateTime(params.value),
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultDateFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.lastModified'),
      field                    : 'lastUpdated',
      valueFormatter           : (params) => formatDateTime(params.value),
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultDateFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.username'),
      field                    : 'initiator.username',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    }
  ];
};

/**
 * @getColumnDefinitionsDesktop
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionsMigrationResultsDesktop = (): ColDef[] => {
  return [
    {
      headerName               : translate('sisense:analytics.id'),
      field                    : 'id',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.migrationId'),
      field                    : 'migrationId',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.account'),
      field                    : 'accountId',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.accountName'),
      field                    : 'accountName',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.username'),
      field                    : 'owner.username',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.name'),
      field                    : 'schedule.name',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.scheduleStatus'),
      field                    : 'schedule.enabled',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.scheduleId'),
      field                    : 'schedule.id',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.scheduleNewId'),
      field                    : 'schedule.newId',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.dashboardName'),
      field                    : 'dashboard.title',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.dashboardId'),
      field                    : 'dashboard.id',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultTextFilterParams,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.success'),
      field                    : 'success',
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      suppressHeaderMenuButton : true
    },
    {
      headerName               : translate('sisense:analytics.created'),
      field                    : 'created',
      valueFormatter           : (params) => formatDateTime(params.value),
      sortable                 : false,
      filter                   : false,
      resizable                : true,
      unSortIcon               : false,
      hide                     : false,
      filterParams             : defaultDateFilterParams,
      suppressHeaderMenuButton : true
    }
  ];
};

const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsDesktop())
});

export default withSignals(MigrationResultsGrid);
